import { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { syncMemberToConstantContact } from '../services/constantContactService';
import {
  enhancedLogin,
  enhancedRegister,
  enhancedLogout,
  hasPermission,
  hasAnyPermission,
  hasRole,
  verifyUserSession,
  USER_ROLES
} from '../services/enhancedAuthService';
import {
  performBotDetection,
  setCurrentUserId,
  getHumanInteractionScore,
  isHumanBehavior
} from '../services/botDetectionService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Enhanced authentication state
  const [botDetectionResult, setBotDetectionResult] = useState(null);
  const [requiresAdditionalVerification, setRequiresAdditionalVerification] = useState(false);
  const [securityFlags, setSecurityFlags] = useState({});

  // Enhanced sign up with email and password
  async function signup(email, password, userData, captchaResponse) {
    try {
      // Combine email and password with userData for enhanced register
      const completeUserData = {
        ...userData,
        email: email,
        password: password
      };

      const result = await enhancedRegister(completeUserData, captchaResponse);

      // Sync to Constant Contact (async, don't block registration)
      try {
        await syncMemberToConstantContact({
          ...userData,
          email: email,
          memberSince: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error syncing to Constant Contact:', error);
        // Don't fail registration if CC sync fails
      }

      return result;
    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
      throw error;
    }
  }

  // Enhanced sign in with email and password
  async function login(email, password, captchaResponse = null) {
    try {
      const result = await enhancedLogin(email, password, captchaResponse);

      // Update state with enhanced authentication data
      setCurrentUser(result.user);
      setUserProfile(result.userData);
      setRequiresAdditionalVerification(result.requiresAdditionalVerification);

      await fetchUserData(result.user.uid);
      return result;
    } catch (error) {
      console.error('Error signing in:', error);
      setError(error.message);
      throw error;
    }
  }

  // Sign in with Google
  async function loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      const userCredential = await signInWithPopup(auth, provider);

      // Validate user data from Google
      if (!userCredential.user.email) {
        throw new Error('Unable to retrieve email from Google account. Please try again or use email signup.');
      }

      // Check if user profile exists, if not create one
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

      if (!userDoc.exists()) {
        // Extract first and last name from display name
        const nameParts = (userCredential.user.displayName || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        await setDoc(doc(db, 'users', userCredential.user.uid), {
          displayName: userCredential.user.displayName || '',
          firstName: firstName,
          lastName: lastName,
          email: userCredential.user.email,
          photoURL: userCredential.user.photoURL || '',
          role: 'pending',
          status: 'pending',
          memberSince: null, // Will be set when approved
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          loginCount: 0,
          company: '',
          jobTitle: '',
          phone: '',
          linkedinUrl: '',
          aboutMe: ''
        });
      }

      await fetchUserData(userCredential.user.uid);
      return userCredential;
    } catch (error) {
      console.error('Error signing in with Google:', error);

      // Provide more specific error messages
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Google sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Pop-up was blocked by your browser. Please allow pop-ups and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Another sign-in attempt is in progress. Please wait and try again.');
      } else {
        throw new Error('Failed to sign in with Google. Please try again or use email signup.');
      }
    }
  }

  // Reset password
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Update user profile
  async function updateUserProfile(userId, updates) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Refresh user data
      await fetchUserData(userId);
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Fetch user data from Firestore
  async function fetchUserData(userId) {
    try {
      // Fetch user profile
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        setUserProfile({ id: userDoc.id, ...userDoc.data() });
      }

      // Fetch membership data
      const membershipDoc = await getDoc(doc(db, 'memberships', userId));
      if (membershipDoc.exists()) {
        setMembership({ id: membershipDoc.id, ...membershipDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }

  // Enhanced sign out
  async function logout() {
    try {
      await enhancedLogout();
      setCurrentUser(null);
      setUserProfile(null);
      setMembership(null);
      setBotDetectionResult(null);
      setRequiresAdditionalVerification(false);
      setSecurityFlags({});
      setError(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Enhanced authentication methods
  async function performSecurityCheck() {
    try {
      const botDetection = await performBotDetection(currentUser?.uid);
      setBotDetectionResult(botDetection);

      if (currentUser) {
        const sessionVerification = await verifyUserSession(currentUser);
        if (!sessionVerification.valid) {
          await logout();
          throw new Error(sessionVerification.reason);
        }

        setRequiresAdditionalVerification(sessionVerification.requiresAdditionalVerification);
      }

      return botDetection;
    } catch (error) {
      console.error('Security check failed:', error);
      throw error;
    }
  }

  // Check user permissions
  function checkPermission(permission) {
    return hasPermission(userProfile?.role, permission);
  }

  // Check if user has any of the specified permissions
  function checkAnyPermission(permissions) {
    return hasAnyPermission(userProfile?.role, permissions);
  }

  // Check if user has required role
  function checkRole(requiredRole) {
    return hasRole(userProfile?.role, requiredRole);
  }

  // Check if user is admin
  function isAdmin() {
    return checkRole(USER_ROLES.ADMIN);
  }

  // Check if user is super admin
  function isSuperAdmin() {
    return checkRole(USER_ROLES.SUPER_ADMIN);
  }

  // Check if user is active member
  function isActiveMember() {
    if (!membership) return false;

    const now = new Date();
    const endDate = membership.endDate?.toDate();

    return membership.status === 'active' && endDate && endDate > now;
  }

  // Check if user shows human behavior patterns
  function checkHumanBehavior() {
    if (!currentUser) return false;
    return isHumanBehavior(currentUser.uid);
  }

  // Get human interaction score
  function getHumanScore() {
    if (!currentUser) return 0;
    return getHumanInteractionScore(currentUser.uid);
  }

  // Check if user has specific role (legacy method for backward compatibility)
  function hasRoleExact(role) {
    console.log('🔍 Checking exact role:', {
      requestedRole: role,
      userRole: userProfile?.role,
      userProfile: userProfile,
      hasRole: userProfile?.role === role
    });
    return userProfile?.role === role;
  }

  // Listen for auth state changes with enhanced security
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          // Set user ID for human interaction tracking
          setCurrentUserId(user.uid);

          // Verify user session and check for security issues
          const sessionVerification = await verifyUserSession(user);

          if (sessionVerification.valid) {
            await fetchUserData(user.uid);
            setRequiresAdditionalVerification(sessionVerification.requiresAdditionalVerification);

            // Perform periodic security check
            try {
              await performSecurityCheck();
            } catch (securityError) {
              console.warn('Security check failed:', securityError);
            }
          } else {
            // Session invalid, sign out
            console.warn('Invalid session:', sessionVerification.reason);
            await signOut(auth);
            setError(sessionVerification.reason);
          }
        } catch (error) {
          console.error('Error during session verification:', error);
          setUserProfile(null);
          setMembership(null);
        }
      } else {
        // Clear user ID from interaction tracking
        setCurrentUserId(null);
        setUserProfile(null);
        setMembership(null);
        setBotDetectionResult(null);
        setRequiresAdditionalVerification(false);
        setSecurityFlags({});
        setError(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    membership,
    loading,
    error,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    updateUserProfile,
    fetchUserData,
    isActiveMember,
    hasRole: checkRole, // Use enhanced hierarchical role checking

    // Enhanced authentication methods
    performSecurityCheck,
    checkPermission,
    checkAnyPermission,
    checkRole,
    isAdmin,
    isSuperAdmin,
    checkHumanBehavior,
    getHumanScore,

    // Enhanced authentication state
    botDetectionResult,
    requiresAdditionalVerification,
    securityFlags,

    // User roles constants
    USER_ROLES
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
